// src/App.js - VADIEG - Estructura Profesional
import { HashRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// ============================================
// ESTILOS
// ============================================
// Todas las hojas globales (Bootstrap, tokens.css, theme.css…) se importan en
// index.js, en un orden fijo. El JS de Bootstrap (bootstrap.bundle, ~80 KB con
// Popper) no se usaba: ningun componente usa sus desplegables, modales ni
// data-bs-toggle; solo las clases CSS.

// ============================================
// CONTEXT
// ============================================
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from './context/AuthContext';

// ============================================
// HOOKS
// ============================================
import useWindowClose from './hooks/useWindowClose';
import usePingSesion from './hooks/usePingSesion';
import { useIsMobile } from './hooks/useIsMobile';

// ============================================
// COMMON COMPONENTS (NO LAZY - Necesarios inmediatamente)
// ============================================
import ErrorBoundary from './components/common/ErrorBoundary';
import ToastProvider from './components/common/ToastProvider';
import LoadingFallback from './components/common/LoadingFallback';

// ============================================
// API
// ============================================
import { logout as logoutApi } from './api/authApi';

// ============================================
// COMPONENTS - LAYOUT (NO LAZY - Necesarios inmediatamente)
// ============================================
import Header from './components/layout/Header';
import AppSidebar from './components/layout/AppSidebar';

// ============================================
// COMPONENTS - MODALS (NO LAZY - Necesarios inmediatamente)
// ============================================
import IdleTimerWrapper from './components/modals/IdleTimerWrapper';
import { INACTIVIDAD_MS } from './utils/inactividad';

// ============================================
// LAZY LOADING - PAGES PÚBLICAS
// ============================================
const LandingPage = lazy(() => import('./pages/LandingPage'));

// ============================================
// LAZY LOADING - FEATURE: AUTH
// ============================================
const LoginPage = lazy(() => import('./features/auth/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./features/auth/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./features/auth/pages/ResetPasswordPage'));

// ============================================
// LAZY LOADING - FEATURE: DASHBOARD
// ============================================
const Dashboard = lazy(() => import('./features/dashboard/pages/Dashboard'));

// ============================================
// LAZY LOADING - FEATURE: REPORTES
// ============================================
const ReportesPage = lazy(() => import('./features/reportes/pages/ReportesPage'));
const ReportesPartesDiarios = lazy(() => import('./features/reportes/pages/ReportesPartesDiarios'));
const ReportesCombustibles = lazy(() => import('./features/reportes/pages/ReportesCombustibles'));
const ReportesUtilizacion = lazy(() => import('./features/reportes/pages/ReportesUtilizacion'));
const ReportesQuincenal = lazy(() => import('./features/reportes/pages/ReportesQuincenal'));

// ============================================
// LAZY LOADING - FEATURE: AGENDA
// ============================================
const AgendaPage = lazy(() => import('./features/agenda/pages/AgendaPage'));

// ============================================
// LAZY LOADING - FEATURE: CONTACTOS
// ============================================
const ContactosPage = lazy(() => import('./features/contactos/pages/ContactosPage'));

// ============================================

// ============================================
// LAZY LOADING - FEATURE: PARTES DIARIOS
// ============================================
const CrearParteDiario = lazy(() => import('./features/partes/pages/CrearParteDiario'));
const ListaPartesDiarios = lazy(() => import('./features/partes/pages/ListaParteDiario.js'));
const IncidenciasPartes  = lazy(() => import('./features/partes/pages/IncidenciasPartes.js'));
const PanelMaquinistas   = lazy(() => import('./features/partes/pages/PanelMaquinistas.js'));
const EditarParte = lazy(() => import('./features/partes/pages/EditarParte'));
const AdminEquiposBloqueados = lazy(() => import('./features/partes/pages/AdminEquiposBloqueados'));

// ============================================
// LAZY LOADING - FEATURE: ÓRDENES
// ============================================
const CrearOrden = lazy(() => import('./features/ordenes/pages/CrearOrden'));
const ListaOrdenes = lazy(() => import('./features/ordenes/pages/ListaOrdenes'));
const EditarOrdenes = lazy(() => import('./features/ordenes/pages/EditarOrden'));

// ============================================
// LAZY LOADING - FEATURE: USUARIOS
// ============================================
const UsuarioNuevo = lazy(() => import('./features/usuarios/pages/Usuario_Nuevo'));
const UsuariosLista = lazy(() => import('./features/usuarios/pages/Usuario_Lista'));
const PerfilPage = lazy(() => import('./features/usuarios/pages/PerfilPage'));
const ConfiguracionPage = lazy(() => import('./features/usuarios/pages/ConfiguracionPage'));

// ============================================
// LAZY LOADING - FEATURE: NOVEDADES
// ============================================
const NovedadesPage = lazy(() => import('./features/novedades/pages/NovedadesPage'));
const NovedadesDetalle = lazy(() => import('./features/novedades/pages/NovedadesDetalle'));
const EditarNovedad = lazy(() => import('./features/novedades/pages/EditarNovedad'));


// ============================================
// LAZY LOADING - FEATURE: TICKETS
// ============================================
const TicketsListPage = lazy(() => import('./features/tickets/pages/TicketsListPage'));
const TicketCreatePage = lazy(() => import('./features/tickets/pages/TicketCreatePage'));
const TicketDetailPage = lazy(() => import('./features/tickets/pages/TicketDetailPage'));

// ============================================
// LAZY LOADING - FEATURE: HALLAZGOS
// ============================================
const HallazgosListPage = lazy(() => import('./features/hallazgos/pages/HallazgosListPage'));
const NuevoHallazgoPage = lazy(() => import('./features/hallazgos/pages/NuevoHallazgoPage'));
const DetalleHallazgoPage = lazy(() => import('./features/hallazgos/pages/DetalleHallazgoPage'));
const MisHallazgosPage = lazy(() => import('./features/hallazgos/pages/MisHallazgosPage'));
const ReportesHallazgosPage = lazy(() => import('./features/hallazgos/pages/ReportesHallazgosPage'));
const TiemposCierrePage = lazy(() => import('./features/hallazgos/pages/TiemposCierrePage'));
const NuevoChecklistPage = lazy(() => import('./features/hallazgos/pages/NuevoChecklistPage'));
const DetalleChecklistPage = lazy(() => import('./features/hallazgos/pages/DetalleChecklistPage'));
const MapaHallazgosPage = lazy(() => import('./features/hallazgos/pages/MapaHallazgosPage'));
const EditarHallazgoPage = lazy(() => import('./features/hallazgos/pages/EditarHallazgoPage'));
const DashboardEjecutivoPage = lazy(() => import('./features/dashboard/pages/DashboardEjecutivoPage'));

// ============================================
// LAZY LOADING - FEATURE: ADMIN
// ============================================
const ChangelogAdminPage = lazy(() => import('./features/admin/pages/ChangelogAdminPage'));
const UiKitPage = lazy(() => import('./features/admin/pages/UiKitPage'));
const AdminRoute = lazy(() => import('./features/admin/components/AdminRoute'));
const AdminLayout = lazy(() => import('./features/admin/components/AdminLayout'));
const AdminHome = lazy(() => import('./features/admin/pages/AdminHome'));
const AdminUsuarios = lazy(() => import('./features/admin/pages/AdminUsuarios'));
const AdminObras = lazy(() => import('./features/admin/pages/AdminObras'));
const AdminEquipos = lazy(() => import('./features/admin/pages/AdminEquipos'));
const AdminVinculacionLegajos = lazy(() => import('./features/admin/pages/AdminVinculacionLegajos'));
const AdminTareas = lazy(() => import('./features/admin/pages/AdminTareas'));
const AdminObraTareas = lazy(() => import('./features/admin/pages/AdminObraTareas'));
const AdminLogs = lazy(() => import('./features/admin/pages/AdminLogs'));
const AdminPermisos = lazy(() => import('./features/admin/pages/AdminPermisos'));
const AdminRolAccesos = lazy(() => import('./features/admin/pages/AdminRolAccesos'));
const AdminGruposMail = lazy(() => import('./features/admin/pages/AdminGruposMail'));
const MailConfigPage  = lazy(() => import('./features/admin/pages/MailConfigPage'));
const TiposEquipoPage = lazy(() => import('./features/tipos-equipo/pages/TiposEquipoPage'));
const AdminProtheusPage  = lazy(() => import('./features/admin/pages/AdminProtheusPage'));
const ConexionesPage     = lazy(() => import('./features/admin/pages/ConexionesPage'));
const AdminObrasPermisosPage = lazy(() => import('./features/admin/pages/AdminObrasPermisosPage'));
const AdminAlertasPartesPage = lazy(() => import('./features/admin/pages/AdminAlertasPartesPage'));
const AdminPowerfleetPage    = lazy(() => import('./features/admin/pages/AdminPowerfleetPage'));
const AdminCotizaciones      = lazy(() => import('./features/admin/pages/AdminCotizaciones'));
const EquiposInformaticosPage = lazy(() => import('./features/equipos-informaticos/pages/EquiposInformaticosPage'));
const MonitoreoUsuariosPage = lazy(() => import('./features/monitoreo/pages/MonitoreoUsuariosPage'));
// Remitos Internos: oculto por ahora (oct 2026, pedido del usuario). El codigo y
// la tabla quedan; para volver, descomentar esto, las rutas y la API.
// const RemitosListPage   = lazy(() => import('./features/remitos/pages/RemitosListPage'));
// const RemitoFormPage    = lazy(() => import('./features/remitos/pages/RemitoFormPage'));
// const RemitoDetallePage = lazy(() => import('./features/remitos/pages/RemitoDetallePage'));

const EgresoListPage    = lazy(() => import('./features/egreso/pages/EgresoListPage'));
const EgresoDetallePage = lazy(() => import('./features/egreso/pages/EgresoDetallePage'));
const EgresoPlantillaPage = lazy(() => import('./features/egreso/pages/EgresoPlantillaPage'));

// Servicios Generales
const SolicitudesListPage = lazy(() => import('./features/servicios/pages/SolicitudesListPage'));
const NuevaSolicitudPage = lazy(() => import('./features/servicios/pages/NuevaSolicitudPage'));
const DetalleSolicitudPage = lazy(() => import('./features/servicios/pages/DetalleSolicitudPage'));
const GestionMaterialesSolcomPage = lazy(() => import('./features/servicios/pages/GestionMaterialesPage'));

// Capacitación e Inducciones
const DashboardCapacitacionPage = lazy(() => import('./features/capacitacion/pages/DashboardCapacitacionPage'));
const ChecklistPage = lazy(() => import('./features/capacitacion/pages/ChecklistPage'));
const GestionCapacitacionesPage = lazy(() => import('./features/capacitacion/pages/GestionCapacitacionesPage'));
const GestionMaterialesPage = lazy(() => import('./features/capacitacion/pages/GestionMaterialesPage'));
const GestionPlantillasPage = lazy(() => import('./features/capacitacion/pages/GestionPlantillasPage'));
const DetalleCapacitacionPage = lazy(() => import('./features/capacitacion/pages/DetalleCapacitacionPage'));
const BibliotecaPublicaPage = lazy(() => import('./features/capacitacion/pages/BibliotecaPublicaPage'));


// ============================================
// LAZY LOADING - FEATURE: PARTES DE OBRA (Tablero de Control)
// ============================================
const PartesObraListPage   = lazy(() => import('./features/partes-obra/pages/PartesObraListPage'));
const PartesObraCreatePage = lazy(() => import('./features/partes-obra/pages/PartesObraCreatePage'));
const PartesObraDetailPage = lazy(() => import('./features/partes-obra/pages/PartesObraDetailPage'));
const CuadrillasPage       = lazy(() => import('./features/partes-obra/pages/CuadrillasPage'));
const ClimaPage            = lazy(() => import('./features/partes-obra/pages/ClimaPage'));
const EquiposObraPage      = lazy(() => import('./features/partes-obra/pages/EquiposObraPage'));
const ControlEquiposPage   = lazy(() => import('./features/partes-obra/pages/ControlEquiposPage'));
const EquiposPorObraPage   = lazy(() => import('./features/partes-obra/pages/EquiposPorObraPage'));
const MaestroEquiposPage   = lazy(() => import('./features/partes-obra/pages/MaestroEquiposPage'));
const DashboardObraPage      = lazy(() => import('./features/partes-obra/pages/DashboardObraPage'));
const TableroEjecutivoPage   = lazy(() => import('./features/partes-obra/pages/TableroEjecutivoPage'));
const CurvasSPage            = lazy(() => import('./features/partes-obra/pages/CurvasSPage'));
// BL-21: Presupuestos sale del sistema — el import queda comentado, no borrado.
// const PresupuestosPage       = lazy(() => import('./features/partes-obra/pages/PresupuestosPage'));
const ImportarPartesPage     = lazy(() => import('./features/partes-obra/pages/ImportarPartesPage'));
const CoordinadoresObraPage  = lazy(() => import('./features/partes-obra/pages/CoordinadoresObraPage'));
const PersonalControlPage    = lazy(() => import('./features/partes-obra/pages/PersonalControlPage'));
const HistorialObraPage      = lazy(() => import('./features/partes-obra/pages/HistorialObraPage'));
const ServiceEquiposPage   = lazy(() => import('./features/service-equipos/pages/ServiceEquiposPage'));

// ============================================
// LAZY LOADING - FEATURE: PRODUCCIÓN
// ============================================
const ProduccionLayout    = lazy(() => import('./features/produccion/components/ProduccionLayout'));
const ReportSelectorPage  = lazy(() => import('./features/produccion/pages/ReportSelectorPage'));

// ============================================
// LAZY LOADING - FEATURE: GERENCIAL
// ============================================
const GerencialLayout           = lazy(() => import('./features/gerencial/components/GerencialLayout'));
const GerencialPrestaciones     = lazy(() => import('./features/gerencial/pages/GerencialPrestaciones'));
const GerencialManoDeObra       = lazy(() => import('./features/gerencial/pages/GerencialManoDeObra'));
const GerencialPedidosVenta     = lazy(() => import('./features/gerencial/pages/GerencialPedidosVenta'));
const GerencialMateriales       = lazy(() => import('./features/gerencial/pages/GerencialMateriales'));
const GerencialResumen          = lazy(() => import('./features/gerencial/pages/GerencialResumen'));
const GerencialConfiguracion    = lazy(() => import('./features/gerencial/pages/GerencialConfiguracion'));
const GerencialCurvaS           = lazy(() => import('./features/gerencial/pages/GerencialCurvaS'));
const GerencialPlanReal         = lazy(() => import('./features/gerencial/pages/GerencialPlanReal'));
const GerencialCombComparar     = lazy(() => import('./features/gerencial/pages/GerencialCombustibleComparar'));
const GerencialCombustible      = lazy(() => import('./features/gerencial/pages/GerencialCombustible'));
const ImportarCombustible       = lazy(() => import('./features/gerencial/pages/ImportarCombustiblePage'));
const CargaGasoilPage           = lazy(() => import('./features/combustible/pages/CargaGasoilPage'));
const ControlCombustiblePage    = lazy(() => import('./features/combustible/pages/ControlCombustiblePage'));
const GerencialEquipos          = lazy(() => import('./features/gerencial/pages/GerencialEquipos'));
const GerencialObras            = lazy(() => import('./features/gerencial/pages/GerencialObras'));
const FinanzasDocumentos        = lazy(() => import('./features/gerencial/pages/FinanzasDocumentos'));
const FinanzasTablero           = lazy(() => import('./features/gerencial/pages/FinanzasTablero'));
const FinanzasDeuda             = lazy(() => import('./features/gerencial/pages/FinanzasDeuda'));
const FinanzasPagos             = lazy(() => import('./features/gerencial/pages/FinanzasPagos'));

// ============================================
// LAZY LOADING - FEATURE: AUSENTISMO
// ============================================
const AusentismoListPage      = lazy(() => import('./features/ausentismo/pages/AusentismoListPage'));
const NuevoAusentismoPage     = lazy(() => import('./features/ausentismo/pages/NuevoAusentismoPage'));
const DetalleAusentismoPage   = lazy(() => import('./features/ausentismo/pages/DetalleAusentismoPage'));
const AusentismoDashboardPage = lazy(() => import('./features/ausentismo/pages/AusentismoDashboardPage'));
const PortalVacacionesPage    = lazy(() => import('./features/vacaciones/pages/PortalVacacionesPage'));
// Asistente VADIEG (IA): solo admin (esAdmin en el widget y en /bot). Con false
// se oculta para todos, admin incluido.
const ASISTENTE_VISIBLE = true;
const BotPage                = lazy(() => import('./features/bot/pages/BotPage'));
const BotWidget               = lazy(() => import('./components/common/BotWidget'));

// ============================================
// APP PRINCIPAL
// ============================================
function App() {
  return (
    <ThemeProvider>
      <ErrorBoundary>
        <AuthProvider>
          <HashRouter>
            <ToastProvider />
            <AppContent />
          </HashRouter>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  );
}


// ============================================
// BANNER OFFLINE
// ============================================
function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const on  = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online',  on);
    window.addEventListener('offline', off);
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off); };
  }, []);
  if (!offline) return null;
  return (
    <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999, background: '#1f2937', color: '#fff', padding: '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: '0.85rem', fontWeight: 600 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2.5"><path d="M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01"/></svg>
      Sin conexión — Modo offline activo
    </div>
  );
}

// ============================================
// CONTENIDO DE LA APP CON IDLE TIMER
// ============================================
const RUTAS_SIN_SIDEBAR = ['/login', '/forgot-password', '/reset-password'];
const PREFIJOS_SIN_SIDEBAR = ['/admin', '/gerencial', '/partes-obra', '/partes/', '/service-equipos', '/carga-gasoil'];

// Reportes y tableros ejecutivos: fuera del alcance de los roles operativos de obra
const PERMS_EJECUTIVO = ['ROL-01', 'ROL-11', 'reports.executive:read', 'exec.dashboard:read'];
// Presupuestos de licitación: suma al jefe de obra, que ve el cómputo de la suya
// const PERMS_PRESUPUESTOS = ['ROL-01', 'ROL-11', 'exec.budgets:read'];

function useSidebarVisible() {
  const { usuario } = useAuth();
  const { pathname } = useLocation();
  if (!usuario) return false;
  if (RUTAS_SIN_SIDEBAR.includes(pathname)) return false;
  if (PREFIJOS_SIN_SIDEBAR.some(p => pathname.startsWith(p))) return false;
  return true;
}

function AppContent() {
  const { usuario, logout: logoutContext, esAdmin } = useAuth();
  const navigate = useNavigate();
  const sidebarVisible = useSidebarVisible();
  const isMobile = useIsMobile();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // En escritorio el menú se puede ocultar; se recuerda en este navegador.
  const [sidebarOculto, setSidebarOculto] = useState(() => {
    try { return localStorage.getItem('ui.sidebarOculto') === '1'; } catch { return false; }
  });
  const alternarSidebar = () => setSidebarOculto(v => {
    try { localStorage.setItem('ui.sidebarOculto', v ? '0' : '1'); } catch { /* sin storage */ }
    return !v;
  });

  // DEBUG
  console.log('👤 Usuario:', usuario?.id, usuario?.username, usuario?.sessionId);

  // Hook para cerrar sesión al cerrar ventana (MEJORADO)
  useWindowClose(usuario?.id, usuario?.username, usuario?.sessionId);

  // Sesiones en vivo para Admin > Monitoreo
  usePingSesion(!!usuario);

  // Función para manejar el cierre de sesión por inactividad
  const handleIdleLogout = async () => {
    console.log('⏰ Cerrando sesión por inactividad...');

    try {
      await logoutApi();
    } catch (error) {
      console.error('Error al llamar logout API:', error);
    } finally {
      logoutContext();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="app-bg" style={{ marginLeft: (sidebarVisible && !isMobile && !sidebarOculto) ? '240px' : '0', transition: 'margin-left 0.2s' }}>
      <OfflineBanner />
      {usuario && (
        <IdleTimerWrapper
          onLogout={handleIdleLogout}
          timeout={INACTIVIDAD_MS}   // 2 hs (oct 2026: carga del parte en varios momentos)
          warningTime={30000}
        />
      )}

      {sidebarVisible && <AppSidebar mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)}
        oculto={!isMobile && sidebarOculto} onOcultar={alternarSidebar} />}

      {/* Escritorio con el menú oculto: botón para volver a mostrarlo */}
      {sidebarVisible && !isMobile && sidebarOculto && (
        <button
          onClick={alternarSidebar}
          title="Mostrar el menú"
          aria-label="Mostrar el menú"
          style={{
            position: 'fixed', top: 12, left: 12, zIndex: 991, height: 36, width: 36,
            background: '#0d1117', border: '1px solid #1e293b', borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#f1f5f9', fontSize: 18, lineHeight: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
        >
          ☰
        </button>
      )}
      {!sidebarVisible && <ConditionalHeader />}

      {/* Botón hamburguesa — solo visible en mobile cuando hay sidebar */}
      {sidebarVisible && isMobile && (
        <button
          onClick={() => setMobileSidebarOpen(o => !o)}
          style={{
            position: 'fixed', top: 10, left: 10, zIndex: 991,
            background: '#0d1117', border: '1px solid #1e293b',
            borderRadius: 8, width: 36, height: 36,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', color: '#f1f5f9', fontSize: 20, lineHeight: 1,
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}
          aria-label="Menú"
        >
          ☰
        </button>
      )}

      {/* Asistente VADIEG: solo admin. Va a la derecha del menú lateral si está abierto. */}
      {ASISTENTE_VISIBLE && usuario && esAdmin() && (
        <Suspense fallback={null}>
          <BotWidget izquierda={(sidebarVisible && !isMobile && !sidebarOculto) ? 260 : 20} />
        </Suspense>
      )}

      <Suspense fallback={<LoadingFallback />}>
        <Routes>
        {/* ==================== RUTAS PÚBLICAS ==================== */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* ==================== RUTAS PROTEGIDAS ==================== */}
        
        {/* Landing / Home */}
        <Route
          path="/"
          element={
            <RequireAuth>
              <LandingPage />
            </RequireAuth>
          }
        />


        {/* ========== FEATURE: PERFIL Y CONFIGURACIÓN ========== */}
        <Route
          path="/perfil"
          element={
            <RequireAuth>
              <PerfilPage />
            </RequireAuth>
          }
        />
        <Route
          path="/configuracion"
          element={
            <RequireAuth>
              <ConfiguracionPage />
            </RequireAuth>
          }
        />

        {/* ========== ASISTENTE VADIEG (IA): solo admin ========== */}
        <Route
          path="/bot"
          element={
            <RequireAuth>
              <RequireAdminIA>
                <BotPage />
              </RequireAdminIA>
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: AGENDA ========== */}
        <Route
          path="/agenda"
          element={
            <RequireAuth>
              <AgendaPage />
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: CONTACTOS ========== */}
        <Route
          path="/contactos"
          element={
            <RequireAuth>
              <ContactosPage />
            </RequireAuth>
          }
        />



        {/* ========== FEATURE: NOVEDADES ========== */}
        <Route
          path="/novedades"
          element={
            <RequireAuth>
              <NovedadesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/novedades/:id"
          element={
            <RequireAuth>
              <NovedadesDetalle />
            </RequireAuth>
          }
        />
        <Route
          path="/novedades/editar/:id"
          element={
            <RequireAuth>
              <EditarNovedad />
            </RequireAuth>
          }
        />

        {/* ========== MÓDULO: PRODUCCIÓN ========== */}
        <Route element={<RequireAuth><ProduccionLayout /></RequireAuth>}>
          {/* Partes Diarios (Maquinistas) */}
          <Route path="/partes/lista"      element={<RequirePerms anyOf={['produccion', 'ROL-01', 'ops.machinist-logs:read:all', 'ops.machinist-logs:read:own']}><ListaPartesDiarios /></RequirePerms>} />
          <Route path="/partes/incidencias" element={<RequirePerms anyOf={['produccion', 'ops.machinist-logs:read:all', 'ops.machinist-logs:read:own', 'ops.machinist-logs:manage']}><IncidenciasPartes /></RequirePerms>} />
          <Route path="/partes/panel" element={<RequirePerms anyOf={['produccion', 'ops.machinist-logs:read:all', 'ops.machinist-logs:manage']}><SinRol roles={['ROL-09']}><PanelMaquinistas /></SinRol></RequirePerms>} />
          <Route path="/partes/nuevo"      element={<RequirePerms anyOf={['produccion', 'ROL-01', 'ops.machinist-logs:create', 'ops.machinist-logs:manage']}><CrearParteDiario /></RequirePerms>} />
          <Route path="/partes/editar/:id" element={<RequirePerms anyOf={['produccion', 'ROL-01', 'ops.machinist-logs:write:own', 'ops.machinist-logs:manage']}><EditarParte /></RequirePerms>} />
          {/* Partes de Obra */}
          <Route path="/partes-obra"                          element={<PartesObraListPage />} />
          <Route path="/partes-obra/nuevo"                    element={<PartesObraCreatePage />} />
          <Route path="/partes-obra/cuadrillas"               element={<RequirePerms anyOf={['ROL-01', 'cuadrillas:admin', 'ops.crews:read', 'ops.crews:write', 'ops.crews:manage']}><CuadrillasPage /></RequirePerms>} />
          <Route path="/partes-obra/clima"                    element={<RequireAdmin><ClimaPage /></RequireAdmin>} />
          <Route path="/partes-obra/equipos"                  element={<RequirePerms anyOf={['ROL-01', 'ops.fleet:read', 'ops.fleet:write', 'ops.fleet:manage']}><EquiposObraPage /></RequirePerms>} />
          <Route path="/partes-obra/maestro-equipos"          element={<RequirePerms anyOf={['ROL-01', 'ops.fleet:read', 'ops.fleet:write', 'ops.fleet:manage']}><MaestroEquiposPage /></RequirePerms>} />
          <Route path="/partes-obra/control-equipos"          element={<RequirePerms anyOf={['ROL-01', 'ops.fleet:read', 'ops.fleet:write', 'ops.fleet:manage']}><ControlEquiposPage /></RequirePerms>} />
          <Route path="/partes-obra/equipos-por-obra"         element={<RequirePerms anyOf={['ROL-01', 'ops.fleet:by-site']}><EquiposPorObraPage /></RequirePerms>} />
          <Route path="/partes-obra/importar"                 element={<ImportarPartesPage />} />
          <Route path="/partes-obra/coordinadores"            element={<CoordinadoresObraPage />} />
          <Route path="/partes-obra/tablero-select"           element={<RequirePerms anyOf={PERMS_EJECUTIVO}><ReportSelectorPage /></RequirePerms>} />
          <Route path="/partes-obra/reporte-select"           element={<RequirePerms anyOf={PERMS_EJECUTIVO}><ReportSelectorPage /></RequirePerms>} />
          <Route path="/partes-obra/curvas-s"                 element={<Navigate to="/gerencial/curvas-s" replace />} />
          {/* BL-21: Presupuestos queda fuera del sistema. La ruta cae al catch-all. */}
          <Route path="/partes-obra/historial-select"         element={<Navigate to="/partes-obra/historial" replace />} />
          <Route path="/partes-obra/historial"               element={<HistorialObraPage />} />
          <Route path="/partes-obra/historial/:codigo_obra"  element={<HistorialObraPage />} />
          <Route path="/partes-obra/dashboard/:codigo_obra"   element={<DashboardObraPage />} />
          <Route path="/partes-obra/tablero/:codigo_obra"     element={<RequirePerms anyOf={PERMS_EJECUTIVO}><TableroEjecutivoPage /></RequirePerms>} />
          <Route path="/partes-obra/personal"                   element={<PersonalControlPage />} />
          <Route path="/partes-obra/:id"                      element={<PartesObraDetailPage />} />
          <Route path="/service-equipos"                      element={<RequirePerms anyOf={['ROL-01', 'ops.fleet:manage']}><ServiceEquiposPage /></RequirePerms>} />
          {/* Combustible: la carga la hace el repartidor desde el celular */}
          <Route path="/carga-gasoil"                         element={<CargaGasoilPage />} />
          <Route path="/carga-gasoil/control"                 element={<RequirePerms anyOf={['ROL-01', 'ops.fuel:read']}><ControlCombustiblePage /></RequirePerms>} />
        </Route>

        <Route
          path="/admin/equipos-bloqueados"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01', 'ops.fleet:unblock']}>
                <AdminEquiposBloqueados />
              </RequirePerms>
            </RequireAuth>
          }
        />

        {/* Gerencial — layout con sidebar */}
        <Route element={<RequireAuth><RequirePerms anyOf={['ROL-01', 'ROL-11', 'exec.dashboard:read']}><GerencialLayout /></RequirePerms></RequireAuth>}>
          {/* Se entra directo al tablero de obras (el Dashboard se quitó a pedido, 29/10) */}
          <Route path="/gerencial"              element={<Navigate to="/gerencial/obras" replace />} />
          <Route path="/gerencial/prestaciones" element={<GerencialPrestaciones />} />
          <Route path="/gerencial/mano-de-obra" element={<GerencialManoDeObra />} />
          <Route path="/gerencial/pedidos-venta"element={<GerencialPedidosVenta />} />
          <Route path="/gerencial/materiales"   element={<GerencialMateriales />} />
          <Route path="/gerencial/resumen"      element={<GerencialResumen />} />
          <Route path="/gerencial/curva-s"      element={<GerencialCurvaS />} />
          <Route path="/gerencial/curvas-s"     element={<CurvasSPage />} />
          <Route path="/gerencial/plan-vs-real" element={<GerencialPlanReal />} />
          <Route path="/gerencial/combustible"   element={<GerencialCombustible />} />
          <Route path="/gerencial/combustible/importar" element={<ImportarCombustible />} />
          <Route path="/gerencial/combustible/comparar" element={<GerencialCombComparar />} />
          <Route path="/gerencial/equipos"       element={<GerencialEquipos />} />
          <Route path="/gerencial/obras"         element={<GerencialObras />} />
          <Route path="/gerencial/configuracion" element={<GerencialConfiguracion />} />
          <Route path="/gerencial/finanzas"      element={<FinanzasTablero />} />
          <Route path="/gerencial/finanzas/deuda" element={<FinanzasDeuda />} />
          <Route path="/gerencial/finanzas/pagos" element={<FinanzasPagos />} />
        </Route>

        {/* Finanzas › Documentos: además de gerencia, entra el área de pagos
            (exec.finance-documents:read), que no ve el resto del gerencial */}
        <Route element={<RequireAuth><RequirePerms anyOf={['ROL-01', 'ROL-11', 'exec.dashboard:read', 'exec.finance-documents:read']}><GerencialLayout /></RequirePerms></RequireAuth>}>
          <Route path="/gerencial/finanzas/documentos" element={<FinanzasDocumentos />} />
        </Route>

        {/* ========== FEATURE: EQUIPOS INFORMÁTICOS ========== */}
        <Route path="/equipos-informaticos"   element={<RequireAuth><RequirePerms anyOf={['ROL-01']}><EquiposInformaticosPage /></RequirePerms></RequireAuth>} />

        {/* ========== FEATURE: ÓRDENES ========== */}
        <Route
          path="/ordenes/nueva"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ordenes:crear', 'ROL-01', 'ops.work-orders:create', 'ops.work-orders:manage']}>
                <CrearOrden />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/ordenes/lista"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ordenes:ver', 'ROL-01', 'ops.work-orders:read', 'ops.work-orders:manage']}>
                <ListaOrdenes />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/ordenes/editar/:id"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ordenes:editar', 'ROL-01', 'ops.work-orders:close', 'ops.work-orders:manage']}>
                <EditarOrdenes/>
              </RequirePerms>
            </RequireAuth>
          }
        />
        {/* ========== FEATURE: USUARIOS ========== */}
        <Route
          path="/usuarios/nuevo"
          element={
            <RequireAuth>
              <UsuarioNuevo />
            </RequireAuth>
          }
        />
        <Route
          path="/usuarios/lista"
          element={
            <RequireAuth>
              <UsuariosLista />
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: DASHBOARD (solo admin) ========== */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01']}>
                <Dashboard />
              </RequirePerms>
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: REPORTES (solo admin) ========== */}
        <Route
          path="/reportes"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01']}>
                <ReportesPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/reportes/partes-diarios"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01']}>
                <ReportesPartesDiarios />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/reportes/combustibles"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01']}>
                <ReportesCombustibles />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/reportes/utilizacion"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01']}>
                <ReportesUtilizacion />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/reportes/quincenal"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01']}>
                <ReportesQuincenal />
              </RequirePerms>
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: TICKETS ========== */}
        <Route
          path="/tickets"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['tickets:ver', 'ROL-01', 'shared.tickets:read:own']}>
                <TicketsListPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/tickets/nuevo"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['tickets:crear', 'ROL-01', 'shared.tickets:create']}>
                <TicketCreatePage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/tickets/:id"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['tickets:ver', 'ROL-01', 'shared.tickets:read:own']}>
                <TicketDetailPage />
              </RequirePerms>
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: HALLAZGOS ========== */}
        <Route
          path="/hallazgos"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:ver', 'hallazgos:crear', 'hallazgos:ver_area', 'hse.findings:read', 'hse.findings:write', 'hse.findings:manage']}>
                <HallazgosListPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/nuevo"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:crear', 'hallazgos:ver_area', 'hse.findings:write', 'hse.findings:manage']}>
                <NuevoHallazgoPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/mis-hallazgos"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:ver', 'hallazgos:crear', 'hse.findings:read', 'hse.findings:write', 'hse.findings:manage']}>
                <MisHallazgosPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/reportes"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:reportes', 'hse.reports:read']}>
                <ReportesHallazgosPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/reportes/tiempos-cierre"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:reportes', 'hse.reports:read']}>
                <TiemposCierrePage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/checklist/nuevo"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:crear', 'hallazgos:ver', 'hse.checklists:execute', 'hse.findings:write', 'hse.findings:manage']}>
                <NuevoChecklistPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/mapa"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:ver', 'ROL-01', 'hse.findings:read', 'hse.findings:write', 'hse.findings:manage']}>
                <MapaHallazgosPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/checklist/:id"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:ver', 'hallazgos:crear', 'hse.checklists:execute', 'hse.findings:write', 'hse.findings:manage']}>
                <DetalleChecklistPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/:id/editar"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:editar', 'ROL-01', 'hse.findings:manage']}>
                <EditarHallazgoPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/hallazgos/:id"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['hallazgos:ver', 'hallazgos:crear', 'hallazgos:ver_area', 'hse.findings:read', 'hse.findings:write', 'hse.findings:manage']}>
                <DetalleHallazgoPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/dashboard-ejecutivo"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01', 'ROL-09', 'ROL-10', 'reports.executive:read']}>
                <DashboardEjecutivoPage />
              </RequirePerms>
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: SERVICIOS GENERALES ========== */}
        <Route
          path="/servicios"
          element={
            <RequireAuth>
              <SolicitudesListPage />
            </RequireAuth>
          }
        />
        <Route
          path="/servicios/nueva"
          element={
            <RequireAuth>
              <NuevaSolicitudPage />
            </RequireAuth>
          }
        />
        <Route
          path="/servicios/materiales"
          element={
            <RequireAuth>
              <GestionMaterialesSolcomPage />
            </RequireAuth>
          }
        />
        <Route
          path="/servicios/:id"
          element={
            <RequireAuth>
              <DetalleSolicitudPage />
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: CAPACITACIÓN E INDUCCIONES ========== */}
        <Route
          path="/capacitacion"
          element={
            <RequireAuth>
              <DashboardCapacitacionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/capacitacion/checklist/:plantilla_id"
          element={
            <RequireAuth>
              <ChecklistPage />
            </RequireAuth>
          }
        />
        <Route
          path="/capacitacion/gestionar"
          element={
            <RequireAuth>
              <GestionCapacitacionesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/capacitacion/materiales"
          element={
            <RequireAuth>
              <GestionMaterialesPage />
            </RequireAuth>
          }
        />
        <Route
          path="/capacitacion/plantillas"
          element={
            <RequireAuth>
              <GestionPlantillasPage />
            </RequireAuth>
          }
        />
        <Route
          path="/capacitacion/detalle/:id"
          element={
            <RequireAuth>
              <DetalleCapacitacionPage />
            </RequireAuth>
          }
        />
        <Route
          path="/biblioteca"
          element={
            <RequireAuth>
              <BibliotecaPublicaPage />
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: ADMIN ========== */}
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminHome />} />
            {/* Seguridad */}
            <Route path="usuarios"         element={<AdminUsuarios />} />
            {/* Ata cada usuario de maquinista con su legajo de Protheus */}
            <Route path="vinculacion-legajos" element={<AdminVinculacionLegajos />} />
            <Route path="rol-accesos"      element={<AdminRolAccesos />} />
            <Route path="obras-permisos"   element={<AdminObrasPermisosPage />} />
            <Route path="logs"             element={<AdminLogs />} />
            {/* Operaciones */}
            <Route path="obras"            element={<AdminObras />} />
            <Route path="tareas"           element={<AdminTareas />} />
            <Route path="obra-tareas"      element={<AdminObraTareas />} />
            <Route path="alertas-partes"   element={<AdminAlertasPartesPage />} />
            <Route path="egreso"           element={<EgresoListPage />} />
            <Route path="egreso/plantilla" element={<EgresoPlantillaPage />} />
            <Route path="egreso/:id"       element={<EgresoDetallePage />} />
            {/* Equipamiento */}
            <Route path="equipos"            element={<AdminEquipos />} />
            <Route path="tipos-equipo"       element={<TiposEquipoPage />} />
            <Route path="equipos-informaticos" element={<EquiposInformaticosPage />} />
            <Route path="powerfleet"         element={<AdminPowerfleetPage />} />
            {/* Comunicaciones */}
            <Route path="grupos-mail"  element={<AdminGruposMail />} />
            <Route path="mail-config"  element={<MailConfigPage />} />
            {/* Finanzas */}
            <Route path="cotizaciones" element={<AdminCotizaciones />} />
            {/* Integraciones */}
            <Route path="protheus"     element={<AdminProtheusPage />} />
            {/* Referencia del kit de componentes: sin entrada en el menú, se entra por URL */}
            <Route path="ui-kit"       element={<UiKitPage />} />
            <Route path="conexiones"   element={<ConexionesPage />} />
            {/* Sistema */}
            <Route path="changelog"    element={<ChangelogAdminPage />} />
            <Route path="monitoreo"    element={<MonitoreoUsuariosPage />} />
          </Route>
        </Route>

        {/* ========== FEATURE: AUSENTISMO ========== */}
        <Route
          path="/ausentismo"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01', 'hr.attendance:read', 'hr.attendance:write']}>
                <AusentismoListPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/ausentismo/dashboard"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01', 'hr.reports:read']}>
                <AusentismoDashboardPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/ausentismo/nuevo"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01', 'hr.attendance:write']}>
                <NuevoAusentismoPage />
              </RequirePerms>
            </RequireAuth>
          }
        />
        <Route
          path="/ausentismo/:id"
          element={
            <RequireAuth>
              <RequirePerms anyOf={['ROL-01', 'hr.attendance:read', 'hr.attendance:write']}>
                <DetalleAusentismoPage />
              </RequirePerms>
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: PORTAL DE VACACIONES ========== */}
        <Route
          path="/vacaciones"
          element={
            <RequireAuth>
              {/* Cualquier usuario: la página muestra lo propio según su legajo */}
              <PortalVacacionesPage />
            </RequireAuth>
          }
        />

        {/* ========== FEATURE: REMITOS INTERNOS (oculto, oct 2026) ==========
            Las rutas caen al catch-all.
        <Route path="/remitos" element={<RequireAuth><RemitosListPage /></RequireAuth>} />
        <Route path="/remitos/nuevo" element={<RequireAuth><RemitoFormPage /></RequireAuth>} />
        <Route path="/remitos/:id" element={<RequireAuth><RemitoDetallePage /></RequireAuth>} />
        <Route path="/remitos/:id/editar" element={<RequireAuth><RemitoFormPage /></RequireAuth>} />
        */}


        {/* ========== 404 ========== */}
        <Route path="*" element={<h1 className="text-center mt-5">Próximamente</h1>} />
        </Routes>
      </Suspense>

    </div>
  );
}


// ============================================
// COMPONENTES AUXILIARES
// ============================================

/* Oculta el Header en /admin y páginas de auth */
const RUTAS_SIN_HEADER = ['/login', '/forgot-password', '/reset-password'];
function ConditionalHeader() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return null;
  if (location.pathname.startsWith('/gerencial')) return null;
  if (location.pathname.startsWith('/partes-obra')) return null;
  if (location.pathname.startsWith('/partes/')) return null;
  if (location.pathname.startsWith('/service-equipos')) return null;
  if (location.pathname.startsWith('/carga-gasoil')) return null;
  if (RUTAS_SIN_HEADER.includes(location.pathname)) return null;
  return <Header />;
}

/* Autenticación básica */
function RequireAuth({ children }) {
  const { usuario, isAuthReady } = useAuth();
  const location = useLocation();

  if (!isAuthReady) return <p className="text-center mt-5">Cargando sesión…</p>;
  if (!usuario) return <Navigate to="/login" replace state={{ from: location }} />;
  return children;
}

/* IA solo para admin (oct 2026). Mismo criterio que esAdmin del backend
   (role admin, ROL-01 u org.admin); al resto lo devuelve al inicio. */
function RequireAdminIA({ children }) {
  const { esAdmin } = useAuth();
  return ASISTENTE_VISIBLE && esAdmin() ? children : <Navigate to="/" replace />;
}

/* Permisos en frontend (basado en backend) */
// Deja afuera a un rol puntual aunque tenga el permiso (el admin general entra
// siempre). Lo usa el panel de Control de maquinistas para el administrativo de
// obra, ROL-09 (oct 2026).
function SinRol({ roles = [], children }) {
  const { usuario, tieneRol, esAdmin } = useAuth();
  if (!usuario) return null;
  if (esAdmin() || !roles.some(r => tieneRol(r))) return children;
  return (
    <div className="container py-5">
      <div className="card p-4 text-center">
        <h4 className="mb-2">Sin acceso</h4>
        <p className="mb-0">Esta pantalla no está disponible para tu perfil.</p>
      </div>
    </div>
  );
}

function RequirePerms({ anyOf = [], children }) {
  const { usuario } = useAuth();
  if (!usuario) return null;

  // Admin siempre puede
  if (usuario.role === 'admin') return children;

  const permisos = usuario.permisos || [];
  const accesos = usuario.accesos || [];

  // Si es un array, usar includes
  // Chequear tanto los roles (permisos: ROL-01, ROL-08...)
  // como el nuevo sistema granular (accesos: 'modulo:accion')
  // Los accesos shared.* (tickets propios, agenda, novedades...) los tiene
  // cualquier usuario logueado: igual que en el backend y en el menú.
  const ok =
    anyOf.length === 0 ||
    anyOf.some(key => String(key).startsWith('shared.') || permisos.includes(key) || accesos.includes(key));

  if (ok) return children;

  return (
    <div className="container py-5">
      <div className="card p-4 text-center">
        <h4 className="mb-2">Permiso requerido</h4>
        <p className="text-muted mb-0">
          No tenés permisos suficientes para acceder a esta sección.
        </p>
      </div>
    </div>
  );
}


/* Clima queda reservado a admin: el parte ya trae el clima del día y esta
   pantalla es la que fija el histórico de la obra. */
function RequireAdmin({ children }) {
  const { usuario } = useAuth();
  if (!usuario) return null;
  if (usuario.role === 'admin') return children;
  return (
    <div className="container py-5">
      <div className="card p-4 text-center">
        <h4 className="mb-2">Permiso requerido</h4>
        <p className="text-muted mb-0">
          Esta sección es solo para administradores.
        </p>
      </div>
    </div>
  );
}

export default App;